// --- Dependencies
import React from 'react';
import { useSelector } from 'react-redux';
// --- UI Components
import Button from '@dtci/spellbook/Button';
import { DetailsModal } from '../../../../../components/DetailsModal';
import { Pagination } from '../../../../../components/Pagination';
// --- Hooks
import { useLoading } from '../../../../../utils/hooks/useLoading';
// --- Utils
import { getItemRange } from '../../../../../utils/pagination';
import { fetchLinkedProducts } from '../../../utils';
// --- Components
import { LinkedProductsGrid } from './grid';
// --- Actions
import { useLinkedProductsActions } from '../../../../../store/linkedProducts/useProducts';
import { datastageApi } from '../../../../../api';
import { filterCriteria } from '../../../../../utils/filterCriteria';
import { useSharedSectionsActions } from '../../../../../store/sharedSections/useSharedSections';

/**
 * LinkedProductsModal Component
 * Displays a modal for managing linked products with pagination.
 *
 * @returns {JSX.Element} - Rendered LinkedProductsModal component.
 */
export const LinkedProductsModal = () => {
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const {
        isProductsModalOpen,
        dateAddedMap,
        unconfirmedProductIdList,
        currentPage,
        sortParam,
        totalItems,
        totalPages,
        itemsPerPage,
    } = useSelector((state) => state.linkedProducts);
    const { section, sectionRange, sectionSort } = useSelector((state) => state.sharedSections);
    const { updateLinkedProducts, clearLinkedProducts } = useLinkedProductsActions();
    const { updateSharedSections } = useSharedSectionsActions();

    const fetchSharedSections = async (filters = {}) => {
        try {
            setLoadingTrue();
            const data = await datastageApi.sharedSections(
                filterCriteria(filters, '', 'sharedSections'),
                sectionRange,
                sectionSort,
            );
            updateSharedSections({
                shared: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber,
            });
        } catch (error) {
            console.error('Error fetching shared sections:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Close modal and clear linked products state
    const onCloseMemoModal = async () => {
        clearLinkedProducts();
        await fetchSharedSections();
    };

    // Handle page change for pagination
    const handlePageChange = async (pageNumber) => {
        if (pageNumber !== currentPage) {
            setLoadingTrue();
            try {
                const nextPageRange = getItemRange(pageNumber, itemsPerPage, totalItems);
                const linkedProductsData = await fetchLinkedProducts(
                    section?.id,
                    nextPageRange,
                    sortParam,
                    dateAddedMap,
                    unconfirmedProductIdList,
                );
                updateLinkedProducts(linkedProductsData);
            } catch (error) {
                console.error('Error handling page change:', error);
            } finally {
                setLoadingFalse();
            }
        }
    };

    return (
        <DetailsModal
            isOpen={isProductsModalOpen}
            onClose={onCloseMemoModal}
            modalHeader="Linked Products"
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    {/* Center - Pagination */}
                    <div style={{ flexGrow: 1, textAlign: 'center' }}>
                        <Pagination
                            pageCount={totalPages}
                            onPageChange={(e) => handlePageChange(e.selected)}
                            previousLabel="←"
                            nextLabel="→"
                            forcePage={currentPage}
                        />
                    </div>
                    {/* Right Side - Close Button */}
                    <div>
                        <Button
                            sizeType="small"
                            styleType="link"
                            iconType="fa-solid fa-close"
                            onClick={onCloseMemoModal}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            }
        >
            <LinkedProductsGrid />
        </DetailsModal>

    );
};
